import type { SourceFieldInfo } from './SourceFieldInfo.types';
import { inferType } from './inferType';

/**
 * Extracts a human-readable display value from an object or primitive
 * Prioritizes formattedValue, displayValue, label, or name fields
 * @param value - The value to extract from
 * @returns The display value or the original value
 */
const extractDisplayValue = (value: any): any => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    if ('formattedValue' in value) return value.formattedValue;
    if ('displayValue' in value) return value.displayValue;
    if ('label' in value) return value.label;
    if ('name' in value) return value.name;
    return JSON.stringify(value);
  }

  return value;
};

/**
 * Recursively traverses a product object and builds a map of all fields with their inferred types
 * Creates dot-notation paths for nested properties and extracts display values
 * @param obj - The object to traverse
 * @param prefix - Current path prefix (e.g., "product" or "product.brand")
 * @param map - Map to populate with field information
 */
export const traverseProductObject = (
  obj: any,
  prefix: string,
  map: Map<string, SourceFieldInfo>
): void => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return;
  }

  Object.entries(obj).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const displayValue = extractDisplayValue(value);
      const inferredType = inferType(value, displayValue);

      map.set(path, {
        path,
        rawValue: value,
        displayValue,
        inferredType
      });

      traverseProductObject(value, path, map);
    } else {
      const displayValue = extractDisplayValue(value);
      const inferredType = inferType(value, displayValue);

      map.set(path, {
        path,
        rawValue: value,
        displayValue,
        inferredType
      });
    }
  });
};

